/**
 * -----------------------------------------------------------------------------
 * NCJ COMPONENT: B2B Landing Page Lead Capture Form Handler
 * FILE: ncj-landing-b2b-form.js
 * PURPOSE: Handles B2B Lead Form validation, lazy reCAPTCHA v3 loading,
 *          and async submission to the N8N webhook endpoint.
 * AUTHOR: No Code Jungle (NCJ)
 * LAST MODIFIED: 2026-06-04
 * -----------------------------------------------------------------------------
 */

// 👷‍♂️ Architect Check:
// 1. Target File: assets/ncj-landing-b2b-form.js
// 2. Is it Core? NO (New asset file)
// 3. NCJ Namespace Check: YES (Namespaced classes and variables)
// 4. Strategy: Robust Vanilla ES6+ form handler leveraging absolute defensive coding constraints. Injects grecaptcha lazily, performs inline accessible styling validation (0% visual alerts), isolates form states per card, and dispatches JSON trace payloads to the N8N gateway endpoint.

(function() {
  'use strict';

  // Constants
  const RECAPTCHA_SITE_KEY = "6LdrZ4MrAAAAAPMP_E9s1yTiEF9Z_baoUGMOsLIs";
  const WEBHOOK_ENDPOINT = "https://proxy-shield.proud-sky-e47d.workers.dev/b2b-landing-form";
  const WORKFLOW_HEADER = "z@ZzuqLegLYC9&U^3*zL5iS6oaZB";
  const TRACE_RAY_ID = "w1YZ^404CLE3oFAs3PDL87PI2ci";

  // Persist incoming click IDs (gclid, wbraid, gbraid) in sessionStorage immediately on script execution
  try {
    const initialParams = new URLSearchParams(window.location.search);
    const initialGclid = initialParams.get('gclid') || initialParams.get('wbraid') || initialParams.get('gbraid');
    if (initialGclid) {
      sessionStorage.setItem('ncj_gclid', initialGclid);
    }
  } catch (e) {}

  /**
   * Dynamically loads the reCAPTCHA v3 script if it doesn't already exist.
   */
  function lazyLoadRecaptcha() {
    if (window.grecaptcha) return Promise.resolve();

    window.recaptchaSiteKey = RECAPTCHA_SITE_KEY;

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=' + RECAPTCHA_SITE_KEY;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait until grecaptcha is fully initialized
        let checkCount = 0;
        const interval = setInterval(() => {
          if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
            clearInterval(interval);
            resolve();
          }
          checkCount++;
          if (checkCount > 100) { // Timeout after 5 seconds
            clearInterval(interval);
            reject(new Error("reCAPTCHA script loaded but grecaptcha object not available"));
          }
        }, 50);
      };
      script.onerror = () => reject(new Error("Failed to load reCAPTCHA script"));
      document.head.appendChild(script);
    });
  }

  /**
   * Enforces inline field-level validation and styles elements without layout shift.
   */
  function validateField(input, errorRequiredMsg, errorEmailMsg) {
    let isValid = true;

    // Clear previous errors
    input.classList.remove('invalid');
    const existingError = input.parentNode.querySelector('.ncj-input-error-msg');
    if (existingError) existingError.remove();

    if (input.hasAttribute('required')) {
      if (!input.value.trim()) {
        isValid = false;
      }
    }

    if (isValid && input.type === 'email' && input.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(input.value.trim())) {
        isValid = false;
      }
    }

    if (!isValid) {
      input.classList.add('invalid');
      const group = input.closest('.ncj-floating-group');
      if (group) {
        const trigger = group.querySelector('.ncj-custom-select-trigger');
        if (trigger) trigger.classList.add('invalid');
      }
    } else {
      const group = input.closest('.ncj-floating-group');
      if (group) {
        const trigger = group.querySelector('.ncj-custom-select-trigger');
        if (trigger) trigger.classList.remove('invalid');
      }
    }

    return isValid;
  }

  // Country Code Dictionary with crisp SVG Flags for all EEA countries, UK, CH, US & Global
  const COUNTRY_CONFIGS = [
    { iso: 'FR', dial: '+33', name: 'France', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><g fill-rule="evenodd" stroke-width="1pt"><path fill="#fff" d="M0 0h640v480H0z"/><path fill="#00267b" d="M0 0h213.3v480H0z"/><path fill="#f11810" d="M426.7 0H640v480H426.7z"/></g></svg>' },
    { iso: 'ES', dial: '+34', name: 'España', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#aa151b" d="M0 0h640v480H0z"/><path fill="#f1bf00" d="M0 120h640v240H0z"/></svg>' },
    { iso: 'DE', dial: '+49', name: 'Deutschland', minLen: 10, maxLen: 11, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#000" d="M0 0h640v160H0z"/><path fill="#dd0000" d="M0 160h640v160H0z"/><path fill="#ffce00" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'IT', dial: '+39', name: 'Italia', minLen: 9, maxLen: 10, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><g fill-rule="evenodd" stroke-width="1pt"><path fill="#fff" d="M0 0h640v480H0z"/><path fill="#009246" d="M0 0h213.3v480H0z"/><path fill="#ce2b37" d="M426.7 0H640v480H426.7z"/></g></svg>' },
    { iso: 'BE', dial: '+32', name: 'Belgique / België', minLen: 8, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#000" d="M0 0h213.3v480H0z"/><path fill="#ffd90c" d="M213.3 0h213.4v480H213.3z"/><path fill="#f31830" d="M426.7 0H640v480H426.7z"/></svg>' },
    { iso: 'NL', dial: '+31', name: 'Nederland', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#21468b" d="M0 0h640v480H0z"/><path fill="#fff" d="M0 0h640v320H0z"/><path fill="#ae1c28" d="M0 0h640v160H0z"/></svg>' },
    { iso: 'PT', dial: '+351', name: 'Portugal', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#ff0000" d="M0 0h640v480H0z"/><path fill="#006600" d="M0 0h240v480H0z"/></svg>' },
    { iso: 'AT', dial: '+43', name: 'Österreich', minLen: 10, maxLen: 11, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v480H0z"/><path fill="#c8102e" d="M0 0h640v160H0zm0 320h640v160H0z"/></svg>' },
    { iso: 'BG', dial: '+359', name: 'Bulgaria', minLen: 8, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v160H0z"/><path fill="#00966e" d="M0 160h640v160H0z"/><path fill="#d62612" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'HR', dial: '+385', name: 'Hrvatska (Croatia)', minLen: 8, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#ff0000" d="M0 0h640v160H0z"/><path fill="#fff" d="M0 160h640v160H0z"/><path fill="#0000ff" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'CY', dial: '+357', name: 'Cyprus', minLen: 8, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v480H0z"/><path fill="#d47600" d="M220 140h200v160H220z"/><path fill="#4e7026" d="M200 320h240v40H200z"/></svg>' },
    { iso: 'CZ', dial: '+420', name: 'Česko (Czechia)', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v240H0z"/><path fill="#d7141a" d="M0 240h640v240H0z"/><path fill="#11457e" d="M0 0l320 240L0 480z"/></svg>' },
    { iso: 'DK', dial: '+45', name: 'Danmark', minLen: 8, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#c8102e" d="M0 0h640v480H0z"/><path fill="#fff" d="M180 0h70v480h-70zM0 205h640v70H0z"/></svg>' },
    { iso: 'EE', dial: '+372', name: 'Eesti (Estonia)', minLen: 7, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#0072ce" d="M0 0h640v160H0z"/><path fill="#000" d="M0 160h640v160H0z"/><path fill="#fff" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'FI', dial: '+358', name: 'Suomi (Finland)', minLen: 9, maxLen: 10, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v480H0z"/><path fill="#003580" d="M175 0h110v480H175zM0 185h640v110H0z"/></svg>' },
    { iso: 'GR', dial: '+30', name: 'Greece', minLen: 10, maxLen: 10, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#0d5eaf" d="M0 0h640v480H0z"/><path fill="#fff" d="M0 53.3h640v53.4H0zm0 106.7h640v53.3H0zm0 106.7h640v53.3H0zm0 106.6h640v53.4H0zM0 0h266.7v266.7H0z"/><path fill="#0d5eaf" d="M0 0h106.7v266.7H0zm160 0h106.7v266.7H160zM0 0h266.7v106.7H0zm0 160h266.7v106.7H0z"/><path fill="#fff" d="M106.7 0h53.3v266.7h-53.3zM0 106.7h266.7v53.3H0z"/></svg>' },
    { iso: 'HU', dial: '+36', name: 'Magyarország (Hungary)', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#ce2939" d="M0 0h640v160H0z"/><path fill="#fff" d="M0 160h640v160H0z"/><path fill="#477050" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'IE', dial: '+353', name: 'Ireland', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#009a49" d="M0 0h213.3v480H0z"/><path fill="#fff" d="M213.3 0h213.4v480H213.3z"/><path fill="#ff7900" d="M426.7 0H640v480H426.7z"/></svg>' },
    { iso: 'IS', dial: '+354', name: 'Ísland (Iceland)', minLen: 7, maxLen: 7, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#02529c" d="M0 0h640v480H0z"/><path fill="#fff" d="M160 0h100v480H160zM0 190h640v100H0z"/><path fill="#dc1e35" d="M180 0h60v480h-60zM0 210h640v60H0z"/></svg>' },
    { iso: 'LV', dial: '+371', name: 'Latvija (Latvia)', minLen: 8, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#9e3039" d="M0 0h640v480H0z"/><path fill="#fff" d="M0 192h640v96H0z"/></svg>' },
    { iso: 'LI', dial: '+423', name: 'Liechtenstein', minLen: 7, maxLen: 7, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#002b7f" d="M0 0h640v240H0z"/><path fill="#ce1126" d="M0 240h640v240H0z"/></svg>' },
    { iso: 'LT', dial: '+370', name: 'Lietuva (Lithuania)', minLen: 8, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fdb913" d="M0 0h640v160H0z"/><path fill="#006a44" d="M0 160h640v160H0z"/><path fill="#c1272d" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'LU', dial: '+352', name: 'Luxembourg', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#ea141d" d="M0 0h640v160H0z"/><path fill="#fff" d="M0 160h640v160H0z"/><path fill="#00a3e0" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'MT', dial: '+356', name: 'Malta', minLen: 8, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h320v480H0z"/><path fill="#cf142b" d="M320 0h320v480H320z"/></svg>' },
    { iso: 'NO', dial: '+47', name: 'Norge (Norway)', minLen: 8, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#ba0c2f" d="M0 0h640v480H0z"/><path fill="#fff" d="M160 0h120v480H160zM0 180h640v120H0z"/><path fill="#00205b" d="M190 0h60v480h-60zM0 210h640v60H0z"/></svg>' },
    { iso: 'PL', dial: '+48', name: 'Polska', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v480H0z"/><path fill="#dc143c" d="M0 240h640v240H0z"/></svg>' },
    { iso: 'RO', dial: '+40', name: 'România', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#002b7f" d="M0 0h213.3v480H0z"/><path fill="#fcd116" d="M213.3 0h213.4v480H213.3z"/><path fill="#ce1126" d="M426.7 0H640v480H426.7z"/></svg>' },
    { iso: 'SK', dial: '+421', name: 'Slovensko (Slovakia)', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v160H0z"/><path fill="#0b4ea2" d="M0 160h640v160H0z"/><path fill="#ee1c25" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'SI', dial: '+386', name: 'Slovenija', minLen: 8, maxLen: 8, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#fff" d="M0 0h640v160H0z"/><path fill="#0000ff" d="M0 160h640v160H0z"/><path fill="#ff0000" d="M0 320h640v160H0z"/></svg>' },
    { iso: 'SE', dial: '+46', name: 'Sverige', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#006aa7" d="M0 0h640v480H0z"/><path fill="#fecc00" d="M0 192h640v96H0zM192 0h96v480h-96z"/></svg>' },
    { iso: 'CH', dial: '+41', name: 'Schweiz / Suisse', minLen: 9, maxLen: 9, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#d52b1e" d="M0 0h640v480H0z"/><path fill="#fff" d="M260 100h120v280H260zm-100 100h320v120H160z"/></svg>' },
    { iso: 'GB', dial: '+44', name: 'United Kingdom', minLen: 10, maxLen: 10, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#00247d" d="M0 0h640v480H0z"/><path fill="#fff" d="M0 0l640 480M640 0L0 480" stroke="#fff" stroke-width="60"/><path fill="#cf142b" d="M0 0l640 480M640 0L0 480" stroke="#cf142b" stroke-width="40"/><path fill="#fff" d="M320 0v480M0 240h640" stroke="#fff" stroke-width="100"/><path fill="#cf142b" d="M320 0v480M0 240h640" stroke="#cf142b" stroke-width="60"/></svg>' },
    { iso: 'US', dial: '+1', name: 'United States / Canada', minLen: 10, maxLen: 10, svg: '<svg viewBox="0 0 640 480" width="20" height="15"><path fill="#bd3d44" d="M0 0h640v480H0z"/><path fill="#fff" d="M0 36.9h640v36.9H0zm0 73.8h640v36.9H0zm0 73.8h640v36.9H0zm0 73.8h640v36.9H0zm0 73.8h640v36.9H0zm0 73.8h640v36.9H0z"/><path fill="#192f5d" d="M0 0h256v258.5H0z"/></svg>' },
    { iso: 'OTHER', dial: '+', name: 'Otros países / Other', minLen: 6, maxLen: 14, svg: '<svg viewBox="0 0 24 24" width="20" height="15" fill="none" stroke="#004e4e" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>' }
  ];

  // Global helper to close any active dropdowns
  const closeAllDropdowns = (exceptDropdown = null) => {
    document.querySelectorAll('.ncj-phone-country-dropdown, .ncj-custom-select-dropdown').forEach(dropdown => {
      if (dropdown !== exceptDropdown) {
        dropdown.hidden = true;
        const group = dropdown.closest('.ncj-phone-prefix-group, .ncj-floating-group');
        if (group) {
          const btn = group.querySelector('.ncj-phone-prefix-btn, .ncj-custom-select-trigger');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      }
    });
  };

  document.addEventListener('click', () => {
    closeAllDropdowns();
  });

  /**
   * Binds and configures a specific B2B Lead form instance.
   */
  function initB2BForm(form) {
    const feedbackEl = form.querySelector('.ncj-form-feedback');
    const submitBtn = form.querySelector('button[type="submit"]');
    const defaultBtnText = submitBtn ? submitBtn.textContent : "";

    const errorRequired = form.getAttribute('data-error-required') || "This field is required.";
    const errorEmail = form.getAttribute('data-error-email') || "Please enter a valid email address.";
    const successMsg = form.getAttribute('data-success-msg') || "Thank you! Your request has been successfully submitted.";
    const errorForm = form.getAttribute('data-error-form') || "Please correct the errors in the form.";
    const statusLoading = form.getAttribute('data-status-loading') || "Processing...";
    const errorSubmit = form.getAttribute('data-error-submit') || "Error submitting the form. Please try again or contact support.";

    const labelOther = form.getAttribute('data-label-other') || "Otro";

    // Phone Country Prefix & Live Validation Controller
    const phoneGroup = form.querySelector('.ncj-phone-prefix-group') ? form : null;
    let selectedCountry = COUNTRY_CONFIGS.find(c => c.iso === 'FR'); // Fallback default
    let getFormattedPhone = () => '';

    const prefixBtn = form.querySelector('.ncj-phone-prefix-btn');
    const phoneInput = form.querySelector('.ncj-phone-input');
    const dropdownEl = form.querySelector('.ncj-phone-country-dropdown');
    const countryListEl = form.querySelector('.ncj-phone-country-list');

    if (phoneInput && prefixBtn) {
      const detectedIso = (form.getAttribute('data-detected-country') || 'FR').toUpperCase();
      const detectedMatch = COUNTRY_CONFIGS.find(c => c.iso === detectedIso);
      if (detectedMatch) {
        selectedCountry = detectedMatch;
      }

      const flagEl = prefixBtn.querySelector('.ncj-flag-icon');
      const dialCodeEl = prefixBtn.querySelector('.ncj-prefix-code');
      const customInputEl = prefixBtn.querySelector('.ncj-prefix-custom-input');

      const updatePrefixBtn = () => {
        if (flagEl) flagEl.innerHTML = selectedCountry.svg;
        if (selectedCountry.iso === 'OTHER') {
          if (dialCodeEl) dialCodeEl.style.display = 'none';
          if (customInputEl) {
            customInputEl.style.display = 'inline-block';
            if (!customInputEl.value || customInputEl.value === '+') {
              customInputEl.value = '+';
            }
          }
          prefixBtn.classList.add('has-custom-input');
        } else {
          if (dialCodeEl) {
            dialCodeEl.style.display = 'inline';
            dialCodeEl.textContent = selectedCountry.dial;
          }
          if (customInputEl) customInputEl.style.display = 'none';
          prefixBtn.classList.remove('has-custom-input');
        }
      };

      if (customInputEl) {
        customInputEl.addEventListener('click', (e) => {
          e.stopPropagation();
        });
        customInputEl.addEventListener('input', (e) => {
          e.stopPropagation();
          let val = customInputEl.value;
          let digitsOnly = val.replace(/\D/g, '');
          customInputEl.value = '+' + digitsOnly.substring(0, 4);
          customInputEl.classList.remove('invalid');
          prefixBtn.classList.remove('invalid');
        });
      }

      const renderCountryList = () => {
        if (!countryListEl) return;
        countryListEl.innerHTML = COUNTRY_CONFIGS.map(c => `
          <li class="ncj-phone-country-option ${c.iso === selectedCountry.iso ? 'selected' : ''}" data-iso="${c.iso}">
            <span class="ncj-flag-icon">${c.svg}</span>
            <span class="ncj-country-name">${c.iso === 'OTHER' ? labelOther : c.name}</span>
            <span class="ncj-country-dial">${c.dial}</span>
          </li>
        `).join('');

        countryListEl.querySelectorAll('.ncj-phone-country-option').forEach(opt => {
          opt.addEventListener('click', (e) => {
            e.stopPropagation();
            const iso = opt.getAttribute('data-iso');
            const match = COUNTRY_CONFIGS.find(c => c.iso === iso);
            if (match) {
              selectedCountry = match;
              updatePrefixBtn();
              renderCountryList();
              validatePhoneInput();
              if (selectedCountry.iso === 'OTHER' && customInputEl) {
                setTimeout(() => {
                  customInputEl.focus();
                  const len = customInputEl.value.length;
                  if (typeof customInputEl.setSelectionRange === 'function') {
                    customInputEl.setSelectionRange(len, len);
                  }
                }, 50);
              }
            }
            if (dropdownEl) dropdownEl.hidden = true;
            if (prefixBtn) prefixBtn.setAttribute('aria-expanded', 'false');
          });
        });
      };

      // Strict non-digit input filtering (allows ONLY numbers 0-9)
      phoneInput.addEventListener('input', (e) => {
        let cleanVal = phoneInput.value.replace(/[^0-9]/g, '');
        if (phoneInput.value !== cleanVal) {
          phoneInput.value = cleanVal;
        }
        validatePhoneInput();
      });

      const validatePhoneInput = () => {
        let rawVal = phoneInput.value.trim();

        // Auto-detect pasted full international numbers (e.g. +34612345678 or 0034612345678)
        if (rawVal.startsWith('+') || rawVal.startsWith('00')) {
          const cleanPasted = rawVal.replace(/^00/, '+');
          const matchedCountry = COUNTRY_CONFIGS.find(c => c.dial !== '+' && cleanPasted.startsWith(c.dial));
          if (matchedCountry && matchedCountry.iso !== selectedCountry.iso) {
            selectedCountry = matchedCountry;
            updatePrefixBtn();
            renderCountryList();
            rawVal = cleanPasted.substring(matchedCountry.dial.length);
            phoneInput.value = rawVal;
          }
        }

        let digits = rawVal.replace(/\D/g, '');
        if (digits.startsWith('0')) {
          digits = digits.substring(1);
        }

        return digits.length >= selectedCountry.minLen && digits.length <= selectedCountry.maxLen;
      };

      getFormattedPhone = () => {
        let rawVal = phoneInput.value.trim();
        if (!rawVal) return '';

        let digits = rawVal.replace(/\D/g, '');
        if (digits.startsWith('0')) {
          digits = digits.substring(1);
        }

        if (digits.length === 0) return '';

        if (selectedCountry.iso === 'OTHER') {
          let customVal = customInputEl ? customInputEl.value.trim() : '';
          let customDigits = customVal.replace(/\D/g, '');
          if (customDigits.length === 0) {
            return false; // Prefix missing when phone is entered!
          }
          return '+' + customDigits + digits;
        }

        if (digits.length < selectedCountry.minLen || digits.length > selectedCountry.maxLen) {
          return '';
        }

        const dial = selectedCountry.dial === '+' ? '+' : selectedCountry.dial;
        return dial + digits;
      };

      if (dropdownEl) {
        prefixBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const isHidden = dropdownEl.hidden;
          closeAllDropdowns(dropdownEl);
          dropdownEl.hidden = !isHidden;
          prefixBtn.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
        });
      }

      updatePrefixBtn();
      renderCountryList();
    }

    // Custom Select Dropdown Controller for Business Type & Volume
    form.querySelectorAll('.ncj-floating-group').forEach(group => {
      const select = group.querySelector('select.ncj-floating-select');
      if (!select) return;

      select.style.display = 'none';

      let trigger = group.querySelector('.ncj-custom-select-trigger');
      let dropdown = group.querySelector('.ncj-custom-select-dropdown');
      let selectedText = trigger ? trigger.querySelector('.ncj-selected-text') : null;

      if (!trigger) {
        trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'ncj-custom-select-trigger';
        trigger.setAttribute('aria-expanded', 'false');

        selectedText = document.createElement('span');
        selectedText.className = 'ncj-selected-text';
        trigger.appendChild(selectedText);

        const arrowSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        arrowSvg.setAttribute('class', 'ncj-custom-select-arrow');
        arrowSvg.setAttribute('width', '10');
        arrowSvg.setAttribute('height', '6');
        arrowSvg.setAttribute('viewBox', '0 0 10 6');
        arrowSvg.setAttribute('fill', 'none');
        arrowSvg.innerHTML = '<path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
        group.appendChild(arrowSvg);

        dropdown = document.createElement('div');
        dropdown.className = 'ncj-custom-select-dropdown';
        dropdown.hidden = true;

        const list = document.createElement('ul');
        list.className = 'ncj-custom-select-list';
        dropdown.appendChild(list);
        group.appendChild(trigger);
        group.appendChild(dropdown);
      }

      const updateTriggerText = () => {
        const selectedOpt = select.options[select.selectedIndex];
        if (selectedOpt && selectedOpt.value && selectedText) {
          selectedText.textContent = selectedOpt.text;
          group.classList.add('has-value');
          select.classList.add('has-value');
        } else if (selectedText) {
          selectedText.textContent = '';
          group.classList.remove('has-value');
          select.classList.remove('has-value');
        }
      };

      const options = dropdown.querySelectorAll('.ncj-custom-select-option');
      options.forEach(li => {
        li.addEventListener('click', (e) => {
          e.stopPropagation();
          const val = li.getAttribute('data-value') || li.textContent.trim();
          select.value = val;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          select.dispatchEvent(new Event('blur', { bubbles: true }));
          options.forEach(opt => opt.classList.remove('selected'));
          li.classList.add('selected');
          updateTriggerText();
          dropdown.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        });
      });

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isHidden = dropdown.hidden;
        closeAllDropdowns(dropdown);
        dropdown.hidden = !isHidden;
        trigger.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
      });

      select.addEventListener('change', updateTriggerText);
      updateTriggerText();
    });

    let toastTimer = null;
    const showToast = (msg, type = 'error', duration = 4000) => {
      if (!feedbackEl) return;
      clearTimeout(toastTimer);
      feedbackEl.className = `ncj-form-feedback ${type} show`;
      feedbackEl.textContent = msg;

      if (duration > 0) {
        toastTimer = setTimeout(() => {
          hideToast();
        }, duration);
      }
    };

    const hideToast = () => {
      if (!feedbackEl) return;
      feedbackEl.classList.remove('show');
    };

    // Track input validation & hide toast on typing for fluid UX
    form.querySelectorAll('input, select, textarea').forEach(input => {
      input.addEventListener('blur', () => {
        validateField(input, errorRequired, errorEmail);
      });
      input.addEventListener('input', () => {
        hideToast();
        if (input.classList.contains('invalid')) {
          validateField(input, errorRequired, errorEmail);
        }
      });
    });

    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      hideToast();

      // 1. Perform client-side validations
      let isFormValid = true;
      let firstInvalidEl = null;
      form.querySelectorAll('input, select, textarea').forEach(input => {
        if (!validateField(input, errorRequired, errorEmail)) {
          if (!firstInvalidEl) firstInvalidEl = input;
          isFormValid = false;
        }
      });

      if (!isFormValid) {
        showToast(errorForm, 'error', 4000);
        if (firstInvalidEl) {
          const group = firstInvalidEl.closest('.ncj-floating-group');
          const trigger = group ? group.querySelector('.ncj-custom-select-trigger') : null;
          if (trigger) {
            trigger.focus();
          } else {
            firstInvalidEl.focus();
          }
        }
        return;
      }

      const formattedPhone = getFormattedPhone();
      if (formattedPhone === false) {
        showToast(errorForm, 'error', 4000);
        if (customInputEl) {
          customInputEl.classList.add('invalid');
          customInputEl.focus();
        }
        return;
      }

      // 2. Set loading states
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "...";
      }
      showToast(statusLoading, 'loading', 0);

      try {
        // 3. Load reCAPTCHA lazily if not already present
        await lazyLoadRecaptcha();

        // 4. Retrieve token from reCAPTCHA v3
        const token = await new Promise((resolve, reject) => {
          window.grecaptcha.ready(function() {
            window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'b2b_lead_submission' })
              .then(resolve)
              .catch(reject);
          });
        });

        // 5. Gather form payload data
        const formData = new FormData(form);

        // Capture tracking identifiers with multi-layer fallback
        let gaClientId = '';
        try {
          const gaCookie = document.cookie.split('; ').find(row => row.trim().startsWith('_ga='));
          if (gaCookie) {
            const cidParts = gaCookie.split('=')[1].split('.');
            if (cidParts.length >= 4) {
              gaClientId = cidParts[2] + '.' + cidParts[3];
            }
          }
        } catch (e) {}

        let gaSessionId = '';
        try {
          // Prioritize store primary GA4 cookie (_ga_76W1RMJZBE), then fall back to any active container cookie
          const allCookies = document.cookie.split('; ');
          const gaSessionCookie = allCookies.find(row => row.trim().startsWith('_ga_76W1RMJZBE=')) ||
                                  allCookies.find(row => {
                                    const parts = row.trim().split('=');
                                    return parts[0].startsWith('_ga_') && parts[0] !== '_ga';
                                  });
          if (gaSessionCookie) {
            const val = gaSessionCookie.split('=')[1];
            if (val.startsWith('GS1.')) {
              // Legacy format: GS1.1.1713539122.1.1...
              const parts = val.split('.');
              if (parts.length >= 3) {
                gaSessionId = parts[2];
              }
            } else if (val.startsWith('GS2.')) {
              // New format: GS2.1.s1780659407$o3...
              const match = val.match(/\.s([0-9]+)/);
              if (match) {
                gaSessionId = match[1];
              }
            } else {
              // Universal epoch regex fallback
              const match = val.match(/(?:GS[0-9]\.[0-9]\.|^)(?:s)?([0-9]{9,12})/);
              if (match) {
                gaSessionId = match[1];
              }
            }
          }
        } catch (e) {}

        let gclAw = '';
        try {
          const gclAwCookie = document.cookie.split('; ').find(row => row.trim().startsWith('_gcl_aw='));
          if (gclAwCookie) {
            const val = gclAwCookie.split('=')[1];
            if (val) gclAw = val;
          }
        } catch (e) {}

        // Multi-source GCLID extraction: URL param > sessionStorage > _gcl_aw cookie > _gcl_gb / _gcl_wb
        let gclid = '';
        try {
          const urlParams = new URLSearchParams(window.location.search);
          gclid = urlParams.get('gclid') || urlParams.get('wbraid') || urlParams.get('gbraid') || '';

          if (!gclid) {
            gclid = sessionStorage.getItem('ncj_gclid') || '';
          }

          if (!gclid && gclAw) {
            // _gcl_aw format: GCL.<timestamp>.<gclid>
            const gclParts = gclAw.split('.');
            if (gclParts.length >= 3) {
              gclid = gclParts.slice(2).join('.');
            }
          }

          if (!gclid) {
            // Check other Google click cookies if present
            const otherGcl = document.cookie.split('; ').find(row => {
              const r = row.trim();
              return r.startsWith('_gcl_gb=') || r.startsWith('_gcl_wb=') || r.startsWith('_gcl_dc=');
            });
            if (otherGcl) {
              const val = otherGcl.split('=')[1];
              const parts = val.split('.');
              if (parts.length >= 3) {
                gclid = parts.slice(2).join('.');
              }
            }
          }
        } catch (e) {}

        /*
         * GDPR & PRIVACY-BY-DESIGN COMPLIANCE GUARD:
         * 1. Purpose Limitation: Form fields are processed for B2B account registration
         *    under Legitimate Interest / Contractual obligations (legitimate_interest_consent = GRANTED).
         * 2. Consent Granularity: Third-party Ads/Analytics tracking consent is decoupled from 
         *    form submission and read directly from Shopify's Customer Privacy API (cookie banner status).
         * 3. Data Protection: Any PII forwarded to third-party advertising tools is hashed 
         *    using SHA-256 server-side before transmission.
         * 4. Privacy by Default: If the Customer Privacy API is not yet loaded, consent is denied (Scenario E).
         */
        let consentAnalytics = 'DENIED';
        let consentMarketing = 'DENIED';
        try {
          const privacy = window.Shopify && window.Shopify.customerPrivacy;
          if (privacy) {
            // NOTE: analyticsAllowed() does not exist — correct method is analyticsProcessingAllowed()
            if (typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed()) {
              consentAnalytics = 'GRANTED';
            }
            // Use thirdPartyMarketingAllowed for Ads/attribution; fall back to marketingAllowed
            const marketingFn = typeof privacy.thirdPartyMarketingAllowed === 'function'
              ? privacy.thirdPartyMarketingAllowed
              : typeof privacy.marketingAllowed === 'function'
                ? privacy.marketingAllowed
                : null;
            if (marketingFn && marketingFn.call(privacy)) {
              consentMarketing = 'GRANTED';
            }
          }
        } catch (e) {}

        // Construct cleaned E.164 international phone number
        const formattedPhone = getFormattedPhone() || formData.get('phone') || '';

        const payload = {
          first_name: formData.get('first_name') || '',
          last_name: formData.get('last_name') || '',
          email: formData.get('email') || '',
          company: formData.get('company') || '',
          phone: formattedPhone,
          operation_type: formData.get('operation_type') || '',
          volume: formData.get('volume') || '',
          comment: formData.get('comment') || '',
          recaptchaToken: token,
          rayId: TRACE_RAY_ID,
          locale: window.shopifyLocale || document.documentElement.lang || 'en',
          page_handle: form.getAttribute('data-page-handle') || 'landing-b2b',
          ga_client_id: gaClientId,
          ga_session_id: gaSessionId,
          gclid: gclid,
          gcl_aw: gclAw,
          consent_analytics: consentAnalytics,
          consent_marketing: consentMarketing,
          legitimate_interest_consent: 'GRANTED'
        };

        // 6. Submit data to the N8N gateway webhook
        const response = await fetch(WEBHOOK_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'cocworkflow': WORKFLOW_HEADER
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`Servidor respondió con código ${response.status}`);
        }

        // Publish B2B conversion event to Shopify Storefront Analytics Bus (GTM Custom Pixel / sandboxed safe)
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('b2b_landing_form_submitted', {
            formId: form.getAttribute('data-form-id') || 'unknown',
            email: payload.email,
            company: payload.company
          });
        }

        // 7. Render success state
        const card = form.closest('[class*="-form-card-"]');
        if (card) {
          card.style.transition = 'opacity 0.3s, transform 0.3s';
          card.style.opacity = '0';
          card.style.transform = 'translateY(8px)';

          setTimeout(() => {
            card.innerHTML = `
              <div class="ncj-form-success-container" style="text-align: center; padding: 40px 16px; opacity: 0; transform: translateY(-8px); transition: opacity 0.4s ease, transform 0.4s ease;">
                <div class="ncj-form-success-icon" style="margin-bottom: 24px; display: inline-flex; align-items: center; justify-content: center; width: 60px; height: 60px; background-color: rgba(49, 194, 71, 0.12); border-radius: 50%; color: #31c22f;">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <h3 style="font-size: 22px; font-weight: 800; color: #004e4e; margin: 0 0 14px 0; line-height: 1.2; letter-spacing: -0.01em;">${window.ncjB2BFormTranslations?.successHeading || "Request Received"}</h3>
                <p style="font-size: 14.5px; color: #5a6b6a; line-height: 1.6; margin: 0 0 24px 0;">${successMsg}</p>
                <div style="font-size: 11px; font-weight: 800; color: #f8ac32; letter-spacing: 0.08em; text-transform: uppercase;">
                  ${window.ncjB2BFormTranslations?.successSub || "Enterprise Desk Active"}
                </div>
              </div>
            `;
            card.style.opacity = '1';
            card.style.transform = 'none';
            setTimeout(() => {
              const successContainer = card.querySelector('.ncj-form-success-container');
              if (successContainer) {
                successContainer.style.opacity = '1';
                successContainer.style.transform = 'none';
              }
            }, 50);
          }, 300);
        } else {
          if (feedbackEl) {
            feedbackEl.className = 'ncj-form-feedback success';
            feedbackEl.style.display = '';
            feedbackEl.textContent = successMsg;
          }
          form.querySelectorAll('.ncj-form-row, .ncj-form-group, .ncj-form-disclaimer').forEach(el => {
            el.style.display = 'none';
          });
          if (submitBtn) submitBtn.style.display = 'none';
        }

      } catch (err) {
        // 8. Revert loading states on failure
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = defaultBtnText;
        }
        showToast(errorSubmit, 'error', 5000);
      }
    });
  }

  // Dynamic Scroll-to-Top Handler for the B2B conversion button (Translation-Safe)
  function initSmoothScrollToTop() {
    const anchorButtons = document.querySelectorAll('a[href="#ncj-b2b-hero-section"], a[href="#ncj-hero-lead-card"]');
    anchorButtons.forEach(btn => {
      btn.removeAttribute('aria-disabled');
      btn.style.cursor = 'pointer';
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    });
  }

  // Self-executing DOM controller
  function initAllB2BComponents() {
    const forms = document.querySelectorAll('.ncj-b2b-form');
    forms.forEach(form => {
      // Prevent duplicate binding
      if (form.getAttribute('data-ncj-bound') === 'true') return;
      form.setAttribute('data-ncj-bound', 'true');
      initB2BForm(form);
    });

    initSmoothScrollToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllB2BComponents);
  } else {
    initAllB2BComponents();
  }
})();
